/**
 * fichesInteractives.js — Couche d'enrichissement Premium
 *
 * Indexe par `slug` (Plateau OU Circulation) une couche optionnelle qui
 * sera consommée par `CoursCanevasPresentation.jsx` pour injecter des
 * slides spéciales (QCM, Vidéo YouTube, Stat-choc, Schéma SVG animé) dans
 * le Mode Pro Studio générique.
 *
 * Convention :
 *   - injectAfter : "evalDynamique" | "objectif" | "criteres" | "cours"
 *                   | "demonstration" | "guidage" | "autonomie" | "bilan"
 *                   | "title" | "phraseCle" (ou "fin" pour avant la slide finale)
 *   - Chaque entrée du tableau d'enrichissements est une slide à insérer.
 *
 * Aucun enrichissement = comportement actuel inchangé.
 */

// ─── Helpers ────────────────────────────────────────────────────────
const qcm = (question, options, explication, opts = {}) => ({
  kind: 'qcm',
  question,
  options, // [{ label, correct: bool }, ...]
  explication,
  consigne: opts.consigne || 'Lève la main pour répondre',
});

const stat = (valeur, label, detail) => ({
  kind: 'stat',
  valeur,
  label,
  detail,
});

const video = (url, caption) => ({
  kind: 'video',
  videoUrl: url,
  caption,
});

const schema = (variant, props = {}) => ({
  kind: 'schemaSvg',
  variant, // 'freinage7030' | 'slalomS' | 'regle2sec' | 'rondPoint'
  ...props,
});

const phraseChoc = (texte, auteur) => ({
  kind: 'phraseChoc',
  texte,
  auteur,
});

/* ════════════════════════════════════════════════════════════════════
 *  FICHES PILOTES PREMIUM
 * ════════════════════════════════════════════════════════════════════ */

export const FICHES_INTERACTIVES = {
  /* ─────────────────────────────────────────────────────────────────
   * 1) PLATEAU · C1-g · Freinage d'urgence
   * Le 70/30 + V²/150 + QCM répartition + phrase clé
   * ───────────────────────────────────────────────────────────────── */
  'freinage-urgence': {
    badge: 'Plateau · C1-g',
    enrichissements: [
      // Après le cours théorique : schéma 70/30 + stat V²/150
      {
        injectAfter: 'cours',
        slide: schema('freinage7030', {
          title: 'Répartition idéale du freinage',
          subtitle: 'Avant 70 % · Arrière 30 %',
        }),
      },
      {
        injectAfter: 'cours',
        slide: stat(
          'V² / 150',
          'Distance de freinage en mètres',
          'À 50 km/h : 50²/150 = 16,7 m. À 90 km/h : 90²/150 = 54 m. À 130 km/h : 130²/150 = 113 m.',
        ),
      },
      // QCM après démonstration
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'Quelle est la répartition idéale du freinage moto sur route sèche ?',
          [
            { label: '50 % avant · 50 % arrière', correct: false },
            { label: '70 % avant · 30 % arrière', correct: true },
            { label: '30 % avant · 70 % arrière', correct: false },
            { label: '100 % avant uniquement', correct: false },
          ],
          'La règle officielle livret C1-g : 70 % avant, 30 % arrière. Le transfert de masse vers l\'avant lors du freinage rend la roue avant plus efficace. Trop d\'arrière = blocage et chute.',
        ),
      },
      // QCM après guidage
      {
        injectAfter: 'guidage',
        slide: qcm(
          'À 90 km/h, quelle est ta distance de freinage approximative sur route sèche ?',
          [
            { label: 'Environ 27 m', correct: false },
            { label: 'Environ 54 m', correct: true },
            { label: 'Environ 81 m', correct: false },
            { label: 'Environ 113 m', correct: false },
          ],
          'Formule V²/150 → 90² = 8100, divisé par 150 = 54 m. Soit l\'équivalent de 13 places de parking. Ajoute la distance de réaction (≈ 1s = 25 m) → distance totale d\'arrêt ≈ 79 m.',
        ),
      },
      // Phrase choc finale avant bilan
      {
        injectAfter: 'autonomie',
        slide: phraseChoc(
          'Avant 70, arrière 30 — c\'est ma sécurité.',
          'Livret REMC · C1-g',
        ),
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────
   * 2) PLATEAU · C2-f · Slalom — Allure et regard
   * Trajectoire S + QCM regard + stat chrono
   * ───────────────────────────────────────────────────────────────── */
  'slalom-allure': {
    badge: 'Plateau · C2-f',
    enrichissements: [
      {
        injectAfter: 'cours',
        slide: schema('slalomS', {
          title: 'Trajectoire en S — Slalom 5 cônes',
          subtitle: 'Regard porté 2 cônes à l\'avance',
        }),
      },
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'Quand tu passes au niveau du cône n°2, où doit être ton regard ?',
          [
            { label: 'Sur le cône n°2 (celui que tu passes)', correct: false },
            { label: 'Sur le cône n°3 (le suivant)', correct: false },
            { label: 'Sur le cône n°4 (deux cônes à l\'avance)', correct: true },
            { label: 'Sur la ligne d\'arrivée', correct: false },
          ],
          'Règle d\'or du slalom : ton regard est ton volant. Si tu regardes le cône en cours, tu réagis trop tard. Le regard 2 cônes à l\'avance crée la trajectoire fluide en S.',
        ),
      },
      {
        injectAfter: 'guidage',
        slide: stat(
          '≥ 30 km/h',
          'Vitesse minimale de passage à l\'ETM plateau',
          'Mesure : chronométrer le passage entre le 1er et le 5e cône. Si < 4 secondes pour 20 m → vitesse OK.',
        ),
      },
      {
        injectAfter: 'guidage',
        slide: qcm(
          'Faut-il freiner entre les cônes pendant un slalom ?',
          [
            { label: 'Oui, légèrement à chaque cône', correct: false },
            { label: 'Non, jamais — l\'allure doit être constante', correct: true },
            { label: 'Seulement au premier cône', correct: false },
            { label: 'Seulement si la vitesse est trop élevée', correct: false },
          ],
          'Freiner casse la dynamique en S et déstabilise la moto. L\'allure doit être réglée AVANT le slalom et maintenue jusqu\'à la sortie. C\'est le regard + la pression sur les repose-pieds qui pilote la trajectoire.',
        ),
      },
      {
        injectAfter: 'autonomie',
        slide: phraseChoc(
          'Mon regard est mon volant. La moto va où je regarde.',
          'Livret REMC · C2-f',
        ),
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────
   * 3) CIRCULATION · C3 · Distances de sécurité
   * Règle des 2 secondes + chiffres choc + QCM contextes
   * ───────────────────────────────────────────────────────────────── */
  'distances-securite': {
    badge: 'Circulation · C3',
    enrichissements: [
      {
        injectAfter: 'cours',
        slide: schema('regle2sec', {
          title: 'Règle des 2 secondes',
          subtitle: 'Sur route sèche · doubler si pluie',
        }),
      },
      {
        injectAfter: 'cours',
        slide: stat(
          '38 m',
          'Distance parcourue en 1 seconde à 130 km/h',
          'À 130 km/h, tu parcours 36 mètres en 1 seconde. La règle des 2 secondes = 72 m de marge devant le véhicule qui te précède.',
        ),
      },
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'Sur autoroute par temps sec, à 130 km/h, quelle distance minimale dois-tu garder avec le véhicule qui te précède ?',
          [
            { label: '30 mètres', correct: false },
            { label: '50 mètres', correct: false },
            { label: '70 mètres (règle des 2 secondes)', correct: true },
            { label: '100 mètres', correct: false },
          ],
          'La règle des 2 secondes : compter "21 - 22" au moment où le véhicule devant passe un repère fixe. Si tu passes ce repère avant la fin du décompte, tu es trop près. À 130 km/h → ≈ 72 m.',
        ),
      },
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'Sous la pluie, la distance de sécurité doit être :',
          [
            { label: 'Inchangée', correct: false },
            { label: 'Augmentée de 50 %', correct: false },
            { label: 'Doublée (règle des 4 secondes)', correct: true },
            { label: 'Triplée', correct: false },
          ],
          'Pluie = adhérence divisée par 2, distance de freinage doublée. La règle devient celle des 4 secondes. Brouillard ou neige : 5 à 6 secondes. C\'est NON-NÉGOCIABLE en moto.',
        ),
      },
      {
        injectAfter: 'guidage',
        slide: stat(
          '× 2',
          'Distance de freinage sous la pluie',
          'À 90 km/h, 54 m sur sec → 108 m sous la pluie. La distance d\'arrêt totale (réaction + freinage) peut atteindre 130 m.',
        ),
      },
      {
        injectAfter: 'autonomie',
        slide: phraseChoc(
          '21 - 22. Si tu vois passer le repère avant, tu es mort.',
          'Règle des 2 secondes',
        ),
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────
   * 4) CIRCULATION · C3 · Franchir un rond-point
   * Trajectoire SVG + QCM priorité + clignotant
   * ───────────────────────────────────────────────────────────────── */
  'franchir-ronds-points': {
    badge: 'Circulation · C3',
    enrichissements: [
      {
        injectAfter: 'cours',
        slide: schema('rondPoint', {
          title: 'Trajectoire idéale dans un rond-point',
          subtitle: 'Placement · regard · clignotant',
        }),
      },
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'À l\'approche d\'un rond-point (cédez-le-passage), à qui dois-tu céder la priorité ?',
          [
            { label: 'Aux véhicules venant de la droite', correct: false },
            { label: 'Aux véhicules déjà engagés dans l\'anneau', correct: true },
            { label: 'À personne, c\'est toi qui as la priorité', correct: false },
            { label: 'Aux véhicules venant de la gauche uniquement', correct: false },
          ],
          'Sur un rond-point moderne (cédez-le-passage à l\'entrée), la priorité est aux véhicules qui CIRCULENT déjà dans l\'anneau. Attention : un "carrefour à sens giratoire" sans panneau cédez-le-passage = priorité à droite (cas rare mais existant).',
        ),
      },
      {
        injectAfter: 'demonstration',
        slide: qcm(
          'Pour sortir à la deuxième sortie d\'un rond-point, quand mets-tu le clignotant droit ?',
          [
            { label: 'Dès l\'entrée dans le rond-point', correct: false },
            { label: 'Juste avant la 1re sortie (celle que tu ne prends pas)', correct: false },
            { label: 'Juste après la 1re sortie (celle que tu ne prends pas)', correct: true },
            { label: 'Pas besoin de clignotant', correct: false },
          ],
          'Règle : clignotant droit DÈS que tu as dépassé la sortie qui PRÉCÈDE la tienne. Ainsi, les usagers en attente comprennent que tu sors. Erreur fréquente : oublier le clignotant à la sortie d\'un rond-point — c\'est sanctionné à l\'examen.',
        ),
      },
      {
        injectAfter: 'guidage',
        slide: stat(
          '60 %',
          'des motards oublient le clignotant à la sortie d\'un rond-point',
          'Source : observations terrain inspecteurs ETM. C\'est le défaut n°1 sanctionné en circulation sur ce type d\'aménagement.',
        ),
      },
      {
        injectAfter: 'autonomie',
        slide: phraseChoc(
          'Au rond-point : regard, placement, clignotant. Toujours dans cet ordre.',
          'Livret REMC · C3',
        ),
      },
    ],
  },

  /* ════════════════════════════════════════════════════════════════════
   *  PHASE 16 — Premium Circulation bord-de-route (C2 + C3)
   * ════════════════════════════════════════════════════════════════════ */

  /* ─── C2-a · Connaître et respecter les règles ──────────────────── */
  'connaitre-respecter-regles': {
    badge: 'Circulation · C2-a',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('1 pt', "perdu par feu orange grillé (R412-31)", "Code de la route R412-31 : franchissement d'un feu = -4 pts + 135€. Le « feu orange » n'existe pas en droit : il vaut rouge.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu passes un feu qui vient de passer au rouge alors que tu es presque dessus. C'est :",
        [
          { label: 'Toléré si tu n\'avais pas le temps de t\'arrêter', correct: false },
          { label: 'Une infraction : -4 pts + 135€', correct: true },
          { label: 'Une infraction mineure si pas de témoin', correct: false },
          { label: 'Légal si tu es prioritaire', correct: false },
        ],
        "Article R412-30 : tout franchissement de feu rouge = contravention 4e classe. Aucune tolérance. La bonne stratégie : anticiper et freiner au feu orange.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc("Le Code n'est pas une suggestion. C'est ce qui te garde en vie.", 'Livret REMC · C2-a') },
    ],
  },

  /* ─── C2-b · Rechercher signalisation et indices ────────────────── */
  'rechercher-signalisation-indices': {
    badge: 'Circulation · C2-b',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('100 m / 5 s', 'Anticipation minimale en agglomération', "Tu dois balayer la scène 100 m devant ou 5 secondes à l'avance. À 50 km/h, 5 s = 70 m. Sur route, vise 200 m / 8 s.") },
      { injectAfter: 'demonstration', slide: qcm(
        "À l'approche d'un croisement, quel ordre de lecture adoptes-tu ?",
        [
          { label: "Voitures d'abord, puis panneaux", correct: false },
          { label: 'Panneaux → marquage au sol → autres usagers', correct: true },
          { label: 'Marquage au sol uniquement', correct: false },
          { label: 'Tu te fies à ton intuition', correct: false },
        ],
        "Méthode officielle : signalisation verticale (panneaux), puis horizontale (marquage), puis dynamique (autres usagers). Toujours dans cet ordre pour ne rien manquer.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc("Vois TOUT, anticipe TOUT. Tes yeux sont ton ABS le plus efficace.", 'Livret REMC · C2-b') },
    ],
  },

  /* ─── C2-c · Se positionner sur la chaussée ─────────────────────── */
  'positionner-chaussee': {
    badge: 'Circulation · C2-c',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('trajectoireVirage', { title: 'Trajectoire de sécurité en virage', subtitle: 'Extérieur → corde → extérieur · sans toucher la médiane' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Sur une départementale (hors agglomération), à 2 voies, où dois-tu te placer en ligne droite ?",
        [
          { label: "Au milieu de la voie", correct: false },
          { label: "Sur le 1/3 gauche, près de la ligne médiane", correct: false },
          { label: "Sur le 1/3 droit de la voie", correct: true },
          { label: "Sur la trace de roue droite (au plus près du bas-côté)", correct: false },
        ],
        "Hors agglo, on roule dans le 1/3 droit : on est visible des automobilistes en face, on s'écarte de la voie inverse, et on prépare une bonne trajectoire de virage. JAMAIS sur la ligne médiane.",
      ) },
      { injectAfter: 'demonstration', slide: qcm(
        "En agglomération, sur une voie avec stationnement à droite, où dois-tu te placer ?",
        [
          { label: "Au plus près des voitures garées", correct: false },
          { label: "Au centre voire 1/3 gauche de la voie", correct: true },
          { label: "Sur la ligne médiane pour avoir de la marge", correct: false },
          { label: "À droite pour laisser passer les voitures", correct: false },
        ],
        "En agglo, on s'écarte des portières (qui peuvent s'ouvrir) et des piétons qui surgissent : centre ou 1/3 gauche. Mais ATTENTION : ne JAMAIS chevaucher la ligne médiane.",
      ) },
      { injectAfter: 'guidage', slide: phraseChoc(
        'Ligne médiane = INTOUCHABLE. Le reste s\'adapte au contexte.',
        'Livret REMC · C2-c',
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Ma place dépend du contexte — je suis vu, je m\'écarte des dangers.', 'Livret REMC · C2-c') },
    ],
  },

  /* ─── C2-d · Adapter l'allure ───────────────────────────────────── */
  'adapter-allure': {
    badge: 'Circulation · C2-d',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('+ 50 km/h', 'Distance d\'arrêt triplée', "À 50 km/h tu t'arrêtes en ≈ 28 m. À 100 km/h : ≈ 76 m. Ce n'est pas linéaire : la vitesse au carré dicte la distance.") },
      { injectAfter: 'demonstration', slide: qcm(
        "En agglomération, tu vois un piéton sur le trottoir à 20 m. Tu :",
        [
          { label: 'Maintiens ton allure, il est sur le trottoir', correct: false },
          { label: 'Klaxonnes pour le prévenir', correct: false },
          { label: "Ralentis et te tiens prêt à freiner", correct: true },
          { label: 'Accélères pour passer avant qu\'il bouge', correct: false },
        ],
        "Principe d'anticipation : un piéton statique peut s'élancer à tout moment. Tu réduis ta vitesse de 20-30 % et te places pour pouvoir freiner ou éviter.",
      ) },
      { injectAfter: 'guidage', slide: stat('×4', 'Distance d\'arrêt à 100 vs 50 km/h', "Doubler la vitesse multiplie la distance par 4. C'est la loi V². Réduire de 10 km/h en agglo, c'est diminuer ton risque de 30 %.") },
      { injectAfter: 'autonomie', slide: phraseChoc('Mieux vaut arriver en retard que pas du tout.', 'Livret REMC · C2-d') },
    ],
  },

  /* ─── C2-e · Stationnement & redémarrage ────────────────────────── */
  'arret-stationnement-secu': {
    badge: 'Circulation · C2-e',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('11', "départements en ZFE — stationnement contrôlé", "Crit'Air obligatoire dans les ZFE 2026. Stationnement moto : pas sur les trottoirs sauf dans places signalées. Sanction : 35€ + enlèvement.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu te gares en bord de route en descente. Tu :",
        [
          { label: 'Béquille latérale, roue droite côté pente', correct: false },
          { label: 'Béquille latérale, roue vers le haut, 1ère engagée', correct: true },
          { label: 'Béquille centrale uniquement', correct: false },
          { label: 'Tu serres juste le frein avant', correct: false },
        ],
        "En descente : béquille latérale OK mais avant amont (anti-glissement) + 1ère engagée (frein moteur). En montée : pareil mais marche arrière engagée.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Une moto bien garée, c\'est une moto encore là demain.', 'Livret REMC · C2-e') },
    ],
  },

  /* ─── C2-f · Tourner à droite / à gauche ────────────────────────── */
  'tourner-droite-gauche': {
    badge: 'Circulation · C2-f',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('trajectoireVirage', { title: 'Tourner avec trajectoire de sécurité', subtitle: 'Le regard porte. Le corps suit.' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Pour tourner à gauche dans une intersection, tu actives ton clignotant :",
        [
          { label: 'Au moment où tu commences à tourner', correct: false },
          { label: '3 secondes avant la manœuvre', correct: true },
          { label: '10 mètres avant l\'intersection', correct: false },
          { label: 'Pas nécessaire si pas de voiture derrière', correct: false },
        ],
        "Règle Code de la route : clignotant 3 secondes minimum AVANT de changer de trajectoire. C'est une obligation, pas une option. -3 pts si oubli flagrant.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Regarde où tu veux aller. La moto te suit.', 'Livret REMC · C2-f') },
    ],
  },

  /* ─── C2-g · Franchir les intersections ─────────────────────────── */
  'franchir-intersections': {
    badge: 'Circulation · C2-g',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('30 %', "des accidents moto = intersection", "Source ONISR 2024 : 30 % des accidents corporels moto se produisent en intersection. 1 sur 3. La priorité refusée par un automobiliste est la 1ère cause.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu arrives à un STOP. Tu peux franchir si :",
        [
          { label: "Tu ralentis fortement, pied prêt à poser", correct: false },
          { label: "Tu marques un arrêt complet, pied au sol", correct: true },
          { label: "Aucune voiture en vue", correct: false },
          { label: "Tu as la priorité à droite", correct: false },
        ],
        "STOP = arrêt complet OBLIGATOIRE avec pied au sol (preuve d'arrêt total). Cédez-le-passage = ralentir et donner la priorité. -4 pts + 135€ si STOP non marqué.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc("Une intersection, c'est un choix. Anticipe ou subis.", 'Livret REMC · C2-g') },
    ],
  },

  /* ─── C3-b · Croiser, suivre, dépasser ───────────────────────────── */
  'croiser-suivre-depasser': {
    badge: 'Circulation · C3-b',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('1 m', "distance latérale mini en agglomération", "1 m mini en agglo, 1,5 m hors agglo lors d'un dépassement de cycliste, piéton ou véhicule lent. C'est la loi : Article R414-4.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Pour dépasser une voiture sur route, tu vérifies :",
        [
          { label: 'Rétro gauche uniquement', correct: false },
          { label: 'Rétro + angle mort + signalisation devant', correct: true },
          { label: "Juste qu'il n'y a personne devant", correct: false },
          { label: 'Tu klaxonnes avant de te déporter', correct: false },
        ],
        "Méthode officielle : 1) rétro gauche, 2) rotation tête (angle mort), 3) clignotant gauche, 4) déboitement progressif, 5) dépassement franc, 6) clignotant droit, 7) rentre.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Dépasser, c\'est décider. Pas espérer.', 'Livret REMC · C3-b') },
    ],
  },

  /* ─── C3-c · Négocier les virages ────────────────────────────────── */
  'negocier-virages-route': {
    badge: 'Circulation · C3-c',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('trajectoireVirage', { title: 'Trajectoire de sécurité', subtitle: 'Le freinage est TERMINÉ avant l\'entrée' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "En entrée de virage, tu dois :",
        [
          { label: 'Freiner pendant l\'angle pour ralentir', correct: false },
          { label: 'Avoir FINI ton freinage avant l\'inclinaison', correct: true },
          { label: 'Accélérer pour stabiliser la moto', correct: false },
          { label: 'Débrayer pendant le virage', correct: false },
        ],
        "Freiner en virage = transfert de masse + perte d'adhérence = chute. Règle d'or : tout le freinage AVANT, accélération PROGRESSIVE en sortie.",
      ) },
      { injectAfter: 'guidage', slide: stat('63 %', "des accidents moto solo = virages", "Source ONISR 2024 : 63 % des accidents moto sans tiers sont en virage. Vitesse mal adaptée + trajectoire coupée = principales causes.") },
      { injectAfter: 'autonomie', slide: phraseChoc('Le virage se prépare AVANT. Pas pendant.', 'Livret REMC · C3-c') },
    ],
  },

  /* ─── C3-d · Communiquer avec les autres usagers ─────────────────── */
  'communiquer-autres-usagers': {
    badge: 'Circulation · C3-d',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('43 %', "des motards oublient leur clignotant", "Source observation terrain : 43 % des conducteurs moto laissent leur clignotant allumé après changement de direction. Source confusion + accidents.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Un piéton hésite à traverser. Tu :",
        [
          { label: 'Klaxonnes pour l\'inciter à passer', correct: false },
          { label: 'Ralentis et fais un signe de tête pour qu\'il passe', correct: true },
          { label: 'Accélères pour passer avant lui', correct: false },
          { label: 'Tu ignores, il finira par décider', correct: false },
        ],
        "Communication = mouvement de tête, ralentissement visible, contact visuel. Le klaxon est INTERPRÉTÉ comme une agression. Évite sauf urgence réelle.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Ton clignotant, tes signaux, ton regard. Tu existes parce que tu communiques.', 'Livret REMC · C3-d') },
    ],
  },

  /* ─── C3-e · Cohabiter avec les usagers vulnérables ──────────────── */
  'connaitre-autres-usagers': {
    badge: 'Circulation · C3-e',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('1,5 m', "distance mini pour dépasser un cycliste", "Article R414-4 : 1 m mini en agglomération, 1,5 m hors agglomération. Sanction : 135€ + 3 pts si infraction.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu arrives derrière un livreur deux-roues qui zigzague. Tu :",
        [
          { label: 'Dépasses immédiatement par la gauche', correct: false },
          { label: "Attends une zone visible et dépasses avec 1,5 m d'écart", correct: true },
          { label: 'Le suis à 1 m pour le forcer à se ranger', correct: false },
          { label: 'Klaxonnes pour qu\'il se range', correct: false },
        ],
        "Livreur 2-roues = livraison sous pression + trajectoire imprévisible. Tu anticipes (espace, vitesse réduite), tu dépasses large quand visible et stable.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc("L'usager le plus vulnérable a TOUJOURS priorité dans ton attention.", 'Livret REMC · C3-e') },
    ],
  },

  /* ─── C3-f · Cohabiter avec les poids lourds ─────────────────────── */
  'cohabiter-poids-lourds': {
    badge: 'Circulation · C3-f',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('angleMortPL', { title: 'Les 3 angles morts du camion', subtitle: 'Si tu ne vois pas ses rétros, il ne te voit pas' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu es sur autoroute, un camion clignotant à droite. Tu :",
        [
          { label: 'Le doubles par la droite rapidement', correct: false },
          { label: 'Te tiens loin, à au moins 50 m derrière', correct: true },
          { label: 'Te colles à son arrière pour le suivre', correct: false },
          { label: 'Doubles tout de suite par la gauche', correct: false },
        ],
        "PL clignotant = il va changer de voie. Tu te tiens À DISTANCE (50 m) en arrière, jusqu'à ce que sa manœuvre soit terminée. Jamais à côté.",
      ) },
      { injectAfter: 'guidage', slide: stat('14 %', "des accidents motos mortels = avec PL", "Source ONISR 2024. Le poids lourd est sous-représenté dans le trafic mais surreprésenté dans la mortalité moto. Distance + visibilité = vie.") },
      { injectAfter: 'autonomie', slide: phraseChoc('Si tu ne le vois pas dans son rétro, il ne te voit PAS.', 'Livret REMC · C3-f') },
    ],
  },

  /* ─── C3-g · Insertion / sortie voie rapide ──────────────────────── */
  'inserer-sortir-voie-rapide': {
    badge: 'Circulation · C3-g',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('insertionVoieRapide', { title: 'Insertion : utilise TOUTE la bande', subtitle: '50 → 90 → 130 km/h avant de t\'insérer' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu t'insères sur autoroute. La bande d'insertion fait 150 m. Tu :",
        [
          { label: "T'insères immédiatement à 50 km/h", correct: false },
          { label: "Accélères tout au long de la bande pour atteindre 130 avant de t'insérer", correct: true },
          { label: "T'insères au milieu de la bande à 90 km/h", correct: false },
          { label: "Attends que la voie soit vide", correct: false },
        ],
        "Bande d'insertion = espace d'accélération. Objectif : atteindre la vitesse du trafic (110-130 km/h) avant d'aller dans la file. Ne pas s'insérer plus lent = danger.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Une insertion ratée, c\'est un accident probable. Anticipe.', 'Livret REMC · C3-g') },
    ],
  },

  /* ─── C3-h · Circuler en file dense ──────────────────────────────── */
  'circuler-file-dense': {
    badge: 'Circulation · C3-h',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('1 s', "distance minimale en file dense (allure ≤ 30 km/h)", "En file dense, la règle des 2 secondes devient 1 seconde car les vitesses sont basses. Garde quand même l'espace pour pouvoir freiner.") },
      { injectAfter: 'demonstration', slide: qcm(
        "Tu es dans un bouchon à 20 km/h. Tu :",
        [
          { label: 'Tu te colles à la voiture devant pour éviter qu\'on te double', correct: false },
          { label: 'Tu maintiens 1 seconde de distance', correct: true },
          { label: 'Tu zigzagues entre les voitures', correct: false },
          { label: 'Tu poses le pied et tu pousses', correct: false },
        ],
        "Même à 20 km/h, garder 1 seconde = 5 m. Évite les freinages brusques + permet de voir devant. Le zigzag = fatigue + risque ouverture portière.",
      ) },
      { injectAfter: 'autonomie', slide: phraseChoc('Le calme en bouchon, c\'est ton meilleur allié.', 'Livret REMC · C3-h') },
    ],
  },

  /* ─── C3-i · Circulation inter-files (CIF) ───────────────────────── */
  'circulation-inter-files': {
    badge: 'Circulation · C3-i',
    enrichissements: [
      { injectAfter: 'cours', slide: stat('50 max · +20 max', "vitesse en CIF (loi 2025)", "Décret 2025 : CIF autorisée sur axes ≥ 4 voies, vitesse max 50 km/h, écart max +20 km/h avec trafic adjacent. Hors zone : sanction 135€.") },
      { injectAfter: 'demonstration', slide: qcm(
        "La CIF est autorisée :",
        [
          { label: "Partout en France sans condition", correct: false },
          { label: "Uniquement sur axes ≥ 4 voies, expérimentation 2025", correct: true },
          { label: "Uniquement en agglomération", correct: false },
          { label: "Uniquement la nuit", correct: false },
        ],
        "L'expérimentation 2025-2027 autorise la CIF sur 21 départements, sur axes ≥ 4 voies, 50 km/h max, +20 km/h max au-dessus du trafic. Vérifie la signalisation locale.",
      ) },
      { injectAfter: 'guidage', slide: stat('+ 12 %', "d'accidents moto lors de la 1ère phase CIF (2016-2020)", "L'expérimentation précédente a montré une hausse d'accidents inter-files. La 2e phase 2025-2027 affine les règles. Reste hyper-vigilant.") },
      { injectAfter: 'autonomie', slide: phraseChoc("La CIF n'est pas un droit acquis. C'est un privilège conditionnel.", 'Livret REMC · C3-i') },
    ],
  },

  /* ─── C3-j · Adhérence / visibilité réduites (pluie / nuit) ──────── */
  'adherence-visibilite-reduites': {
    badge: 'Circulation · C3-j',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('pluieAdaptation', { title: 'Pluie : adapte-toi en 2 réflexes', subtitle: '−20 % vitesse · ×2 distance' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Sous une pluie battante, à 90 km/h, ta distance de sécurité passe à :",
        [
          { label: '54 m (inchangée)', correct: false },
          { label: '90 m (1,5 ×)', correct: false },
          { label: '108 m (× 2)', correct: true },
          { label: '162 m (× 3)', correct: false },
        ],
        "Règle des 4 secondes par pluie (au lieu de 2). 90 km/h × 4 s = 100 m. Brouillard / neige : 5-6 s = 130-150 m. Adaptation = vie.",
      ) },
      { injectAfter: 'guidage', slide: stat('× 7', "risque d\'accident moto la nuit", "Source ONISR : 1 km parcouru de nuit = 7 × le risque de jour. Vis pour rentrer avant la tombée du soir si possible.") },
      { injectAfter: 'autonomie', slide: phraseChoc('La météo dicte la vitesse, pas le compteur.', 'Livret REMC · C3-j') },
    ],
  },

  /* ─── C3-k · Tunnels et ponts ────────────────────────────────────── */
  'tunnels-ponts': {
    badge: 'Circulation · C3-k',
    enrichissements: [
      { injectAfter: 'cours', slide: schema('tunnelTransition', { title: 'Entrée tunnel : 3 sec d\'adaptation', subtitle: 'Tes yeux, ta visière, tes feux' }) },
      { injectAfter: 'demonstration', slide: qcm(
        "Avant d'entrer dans un tunnel, tu :",
        [
          { label: 'Maintiens ta vitesse, allumes tes feux', correct: false },
          { label: "Relèves ta visière, allumes les feux de croisement, ralentis légèrement", correct: true },
          { label: 'Mets en pleins phares', correct: false },
          { label: 'Tu n\'as rien à modifier', correct: false },
        ],
        "Tunnel = perte de lumière soudaine. Visière relevée pour réduire le contraste, feux de croisement obligatoires, distance doublée (vent latéral), vitesse adaptée.",
      ) },
      { injectAfter: 'guidage', slide: stat('+ 30 %', "de mortalité en moto sur un pont (vent)", "Source études MAIF : les ponts longs sont des spots accidentogènes pour les motards à cause du vent latéral. Anticipe la rafale à mi-pont.") },
      { injectAfter: 'autonomie', slide: phraseChoc("Tunnel ou pont = transition. Toute transition demande de l'attention.", 'Livret REMC · C3-k') },
    ],
  },
};

/**
 * Renvoie le bloc d'enrichissement pour un slug donné, ou null.
 */
export function getInteractifBySlug(slug) {
  return FICHES_INTERACTIVES[slug] || null;
}

/**
 * Renvoie le set de slugs ayant un enrichissement premium.
 */
export function getSlugsPremium() {
  return new Set(Object.keys(FICHES_INTERACTIVES));
}

/**
 * Helper : un slug a-t-il une couche premium ?
 */
export function hasPremium(slug) {
  return Boolean(FICHES_INTERACTIVES[slug]);
}
